<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';
  import { toast } from '../../framework/js/toast.js';
  import { fade } from 'svelte/transition';

  /** When true, render with the wizard-style chrome (eyebrow + centred frame).
   *  When false (default), render as a plain inline card for the AgentHero slot. */
  export let wizard: boolean = false;

  let mode: 'upload' | 'paste' | 'brief' = 'upload';
  let file: File | null = null;
  let pastedText = '';
  let title = '';
  let briefUuid = '';
  let availableBriefs: any[] = [];
  let briefSearch = '';
  let briefDropdownOpen = false;
  let briefActiveIndex = -1;
  let isDragging = false;
  let isAnalyzing = false;
  let progress = 0;
  /** uuid of the analysis once it's been created — lets the user leave
   *  the screen while the generation keeps running in the background. */
  let runningAnalysisUuid: string | null = null;
  /** Flag the local polling loop checks so we can stop waiting when the
   *  user bails out without cancelling the server-side job. */
  let leftAnalyzing = false;

  function leaveAnalyzing() {
    // Stop the local poll loop; the backend job keeps running.
    leftAnalyzing = true;
    isAnalyzing = false;
    progress = 0;
    if (runningAnalysisUuid) {
      push(`/gapfinder/${runningAnalysisUuid}`);
    } else {
      push('/gapfinder');
    }
  }

  const BRIEF_RESULT_LIMIT = 5;

  function briefName(b: any): string {
    return b?.name || b?.xplorit?.name || b?.project_name || 'Untitled brief';
  }

  function briefRefLabel(b: any): string {
    const ref = b?.display_ref || b?.xplorit?.display_ref;
    if (ref) return `REF #${ref}`;
    const short = (b?.uuid || '').slice(0, 8);
    return short ? `#${short}` : '';
  }

  function briefEditedAt(b: any): string {
    const iso = b?.updated_at || b?.brief_created_at || b?.created_at;
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }

  function briefAlignmentBadge(b: any): { label: string; tone: 'ok' | 'warn' | 'danger' | 'none' } {
    const raw = b?.alignment_percent;
    if (raw == null) return { label: '', tone: 'none' };
    const pct = Math.round(Number(raw));
    if (Number.isNaN(pct)) return { label: '', tone: 'none' };
    if (pct >= 76) return { label: `${pct}%`, tone: 'ok' };
    if (pct >= 51) return { label: `${pct}%`, tone: 'warn' };
    return { label: `${pct}%`, tone: 'danger' };
  }

  $: filteredBriefs = (() => {
    const q = briefSearch.trim().toLowerCase();
    const source = availableBriefs || [];
    const matches = q
      ? source.filter((b) => briefName(b).toLowerCase().includes(q))
      : source;
    return matches.slice(0, BRIEF_RESULT_LIMIT);
  })();

  $: selectedBrief = availableBriefs.find((b) => b?.uuid === briefUuid) || null;

  function pickBrief(b: any) {
    briefUuid = b?.uuid || '';
    briefSearch = briefName(b);
    briefDropdownOpen = false;
    briefActiveIndex = -1;
  }

  function clearBriefSelection() {
    briefUuid = '';
    briefSearch = '';
    briefDropdownOpen = true;
    briefActiveIndex = -1;
  }

  function onBriefSearchInput(e: Event) {
    briefSearch = (e.target as HTMLInputElement).value;
    // Typing invalidates any existing selection so canAnalyse reflects reality.
    if (briefUuid && briefSearch !== briefName(selectedBrief)) {
      briefUuid = '';
    }
    briefDropdownOpen = true;
    briefActiveIndex = filteredBriefs.length > 0 ? 0 : -1;
  }

  function onBriefSearchKeydown(e: KeyboardEvent) {
    if (!briefDropdownOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      briefDropdownOpen = true;
      briefActiveIndex = filteredBriefs.length > 0 ? 0 : -1;
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      briefActiveIndex = Math.min(filteredBriefs.length - 1, briefActiveIndex + 1);
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      briefActiveIndex = Math.max(0, briefActiveIndex - 1);
      e.preventDefault();
    } else if (e.key === 'Enter') {
      if (briefActiveIndex >= 0 && filteredBriefs[briefActiveIndex]) {
        pickBrief(filteredBriefs[briefActiveIndex]);
        e.preventDefault();
      }
    } else if (e.key === 'Escape') {
      briefDropdownOpen = false;
    }
  }

  function onBriefSearchBlur() {
    // Delay so a click on a dropdown item registers first.
    setTimeout(() => { briefDropdownOpen = false; }, 120);
  }

  function onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files[0]) file = input.files[0];
  }

  function onDragOver(e: DragEvent) { e.preventDefault(); isDragging = true; }
  function onDragLeave() { isDragging = false; }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    isDragging = false;
    if (e.dataTransfer?.files && e.dataTransfer.files[0]) file = e.dataTransfer.files[0];
  }

  function clearFile() { file = null; }

  async function loadBriefs() {
    if (availableBriefs.length > 0) return;
    try {
      const res: any = await api.getXploritBriefs();
      if (res?.success && Array.isArray(res.data?.list)) availableBriefs = res.data.list;
      else if (res?.success && Array.isArray(res.data)) availableBriefs = res.data;
    } catch (e) { console.error(e); }
  }

  $: if (mode === 'brief') loadBriefs();

  $: canAnalyse =
    (mode === 'upload' && !!file) ||
    (mode === 'paste' && pastedText.trim().length > 0) ||
    (mode === 'brief' && !!briefUuid);

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function analyse() {
    if (isAnalyzing || !canAnalyse) return;
    isAnalyzing = true;
    leftAnalyzing = false;
    runningAnalysisUuid = null;
    progress = 5;
    const progressTimer = setInterval(() => {
      if (progress < 90) progress = Math.min(90, progress + Math.random() * 8);
    }, 800);

    try {
      let createRes: any;
      if (mode === 'upload') {
        const fd = new FormData();
        fd.append('file', file as File);
        if (title) fd.append('title', title);
        createRes = await api.createGapfinderFromUpload(fd);
      } else if (mode === 'paste') {
        createRes = await api.createGapfinderFromText({ content: pastedText, title: title || null });
      } else {
        createRes = await api.createGapfinderFromBrief({ xplorit_brief_uuid: briefUuid });
      }

      if (!createRes?.success) {
        toast.showNotification(false, createRes?.message || 'Could not create analysis.');
        return;
      }
      const analysisUuid = createRes?.data?.analysis?.uuid;
      if (!analysisUuid) { toast.showNotification(false, 'Unexpected response from server.'); return; }
      runningAnalysisUuid = analysisUuid;

      const queueRes: any = await api.enqueueGapfinderGenerationAsync(analysisUuid);
      if (!queueRes?.success) { toast.showNotification(false, queueRes?.message || 'Could not queue analysis.'); return; }
      const jobUuid = queueRes?.data?.job_uuid;
      if (!jobUuid) { toast.showNotification(false, 'Server did not return a job id.'); return; }

      const start = Date.now();
      const maxMs = 3 * 60 * 1000;
      while (Date.now() - start < maxMs) {
        await new Promise(r => setTimeout(r, 2500));
        // User bailed out — stop polling, job keeps running server-side.
        if (leftAnalyzing) { clearInterval(progressTimer); return; }
        const jobRes: any = await api.getAiJobStatus(jobUuid);
        if (leftAnalyzing) { clearInterval(progressTimer); return; }
        const status = jobRes?.data?.status || jobRes?.status;
        if (status === 'completed') {
          progress = 100;
          clearInterval(progressTimer);
          push(`/gapfinder/${analysisUuid}`);
          return;
        }
        if (status === 'failed') {
          clearInterval(progressTimer);
          toast.showNotification(false, jobRes?.data?.error_message || 'Analysis failed.');
          isAnalyzing = false; progress = 0; return;
        }
      }
      clearInterval(progressTimer);
      toast.showNotification(false, 'Timed out waiting for analysis.');
      isAnalyzing = false; progress = 0;
    } catch (e) {
      console.error(e);
      clearInterval(progressTimer);
      toast.showNotification(false, 'Something went wrong.');
      isAnalyzing = false; progress = 0;
    }
  }
</script>

<div class="creator-frame" class:wizard>
  {#if wizard}
    <div class="eyebrow-row">
      <span class="eyebrow">
        <i class="search icon" aria-hidden="true"></i>
        GapFinder Analysis
      </span>
    </div>

  {/if}

  {#if wizard && !isAnalyzing}
    <div class="title-card" in:fade={{ duration: 120 }}>
      <h2 class="step-title">Give this analysis a name</h2>
      <p class="step-sub">A short label makes it easy to find later. You can skip this.</p>

      <div class="labelled-field">
        <label for="gf-title" class="field-label">Analysis title <span class="optional">optional</span></label>
        <input
          id="gf-title"
          class="text-input"
          type="text"
          bind:value={title}
          placeholder="e.g. Q2 value-prop research brief"
          autocomplete="off"
        />
      </div>
    </div>
  {/if}

  <div class="creator" class:analyzing={isAnalyzing}>
    {#if !isAnalyzing}
        <div in:fade={{ duration: 120 }}>
          {#if wizard}
            <h2 class="step-title">Choose a source</h2>
            <p class="step-sub">Upload a document, paste text, or pick an existing brief.</p>
          {/if}

          <!-- Segmented control for source -->
          <div class="segment" role="tablist" aria-label="Source type">
            <button type="button" role="tab" aria-selected={mode === 'upload'} class="seg-btn" class:active={mode === 'upload'} on:click={() => (mode = 'upload')}>
              <i class="cloud upload icon" aria-hidden="true"></i>
              <span>Upload</span>
            </button>
            <button type="button" role="tab" aria-selected={mode === 'paste'} class="seg-btn" class:active={mode === 'paste'} on:click={() => (mode = 'paste')}>
              <i class="paste icon" aria-hidden="true"></i>
              <span>Paste text</span>
            </button>
            <button type="button" role="tab" aria-selected={mode === 'brief'} class="seg-btn" class:active={mode === 'brief'} on:click={() => (mode = 'brief')}>
              <i class="file alternate outline icon" aria-hidden="true"></i>
              <span>From brief</span>
            </button>
          </div>

          <div class="field-area" in:fade={{ duration: 120 }}>
            {#if mode === 'upload'}
              {#if file}
                <div class="file-selected" in:fade={{ duration: 120 }}>
                  <div class="file-icon-chip">
                    <i class="file pdf outline icon" aria-hidden="true"></i>
                  </div>
                  <div class="file-meta">
                    <div class="file-name">{file.name}</div>
                    <div class="file-size">{formatBytes(file.size)}</div>
                  </div>
                  <button type="button" class="change-link" on:click={clearFile} aria-label="Remove selected file">
                    <i class="times icon" aria-hidden="true"></i>
                  </button>
                </div>
              {:else}
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <label
                  class="dropzone"
                  class:dragging={isDragging}
                  on:dragover={onDragOver}
                  on:dragleave={onDragLeave}
                  on:drop={onDrop}
                >
                  <input type="file" accept=".pdf,.doc,.docx,.txt" on:change={onFileSelected} class="file-input" aria-label="Upload brief file" />
                  <div class="dz-icon-chip" aria-hidden="true">
                    <i class="cloud upload icon"></i>
                  </div>
                  <div class="dz-text">
                    <strong>Drop your brief here</strong>
                    <span>or <em>click to browse</em></span>
                  </div>
                  <div class="dz-formats">PDF · DOC · DOCX · TXT</div>
                </label>
              {/if}
            {:else if mode === 'paste'}
              <div class="labelled-field">
                <label for="gf-paste" class="field-label">Brief content</label>
                <textarea
                  id="gf-paste"
                  class="paste-area"
                  placeholder="Paste the full text of your research brief here…"
                  bind:value={pastedText}
                  rows="10"
                  spellcheck="false"
                ></textarea>
              </div>
            {:else}
              <div class="labelled-field">
                <label for="gf-brief" class="field-label">Existing brief</label>
                <div class="brief-combobox">
                  <i class="search icon combo-icon" aria-hidden="true"></i>
                  <input
                    id="gf-brief"
                    type="text"
                    role="combobox"
                    aria-expanded={briefDropdownOpen}
                    aria-controls="gf-brief-listbox"
                    aria-autocomplete="list"
                    aria-activedescendant={briefActiveIndex >= 0 ? `gf-brief-opt-${briefActiveIndex}` : undefined}
                    class="text-input combo-input"
                    placeholder="Search by brief name…"
                    autocomplete="off"
                    spellcheck="false"
                    value={briefSearch}
                    on:input={onBriefSearchInput}
                    on:focus={() => (briefDropdownOpen = true)}
                    on:blur={onBriefSearchBlur}
                    on:keydown={onBriefSearchKeydown}
                  />
                  {#if briefUuid || briefSearch}
                    <button
                      type="button"
                      class="combo-clear"
                      on:click|preventDefault={clearBriefSelection}
                      on:mousedown|preventDefault
                      aria-label="Clear selection"
                    >
                      <i class="times icon" aria-hidden="true"></i>
                    </button>
                  {/if}

                  {#if briefDropdownOpen && filteredBriefs.length > 0}
                    <ul id="gf-brief-listbox" role="listbox" class="combo-list">
                      {#each filteredBriefs as b, idx (b.uuid)}
                        {@const badge = briefAlignmentBadge(b)}
                        <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
                        <li
                          id={`gf-brief-opt-${idx}`}
                          role="option"
                          aria-selected={briefActiveIndex === idx}
                          class="combo-item"
                          class:active={briefActiveIndex === idx}
                          class:selected={b.uuid === briefUuid}
                          on:mousedown|preventDefault={() => pickBrief(b)}
                          on:mouseenter={() => (briefActiveIndex = idx)}
                        >
                          <div class="combo-item-main">
                            <span class="combo-item-name">{briefName(b)}</span>
                            {#if badge.label}
                              <span class="combo-badge tone-{badge.tone}">{badge.label}</span>
                            {/if}
                          </div>
                          <div class="combo-item-meta">
                            {#if briefRefLabel(b)}
                              <span>{briefRefLabel(b)}</span>
                            {/if}
                            {#if briefEditedAt(b)}
                              <span class="meta-sep">·</span>
                              <span>Edited {briefEditedAt(b)}</span>
                            {/if}
                          </div>
                        </li>
                      {/each}
                      {#if availableBriefs.length > BRIEF_RESULT_LIMIT}
                        <li class="combo-hint" role="presentation">
                          Showing top {Math.min(BRIEF_RESULT_LIMIT, filteredBriefs.length)} of {availableBriefs.length}. Type to narrow.
                        </li>
                      {/if}
                    </ul>
                  {:else if briefDropdownOpen && availableBriefs.length > 0 && filteredBriefs.length === 0}
                    <div class="combo-empty">No briefs match "{briefSearch}".</div>
                  {/if}
                </div>
                {#if availableBriefs.length === 0}
                  <p class="field-help">No briefs found for your team yet.</p>
                {/if}
              </div>
            {/if}
          </div>

          {#if !wizard && mode !== 'brief'}
            <div class="labelled-field">
              <label for="gf-title-inline" class="field-label">Analysis title <span class="optional">optional</span></label>
              <input
                id="gf-title-inline"
                class="text-input"
                type="text"
                bind:value={title}
                placeholder="e.g. Q2 value-prop research brief"
                autocomplete="off"
              />
            </div>
          {/if}

          <button type="button" class="analyse-btn" on:click={analyse} disabled={!canAnalyse}>
            <span>Analyse brief</span>
            <i class="angle right icon" aria-hidden="true"></i>
          </button>
        </div>
    {:else}
      <div class="analyzing-body" role="status" aria-live="polite">
        <div class="loader-ring" aria-hidden="true"></div>
        <h3 class="analyzing-title">Analysing your brief</h3>
        <p class="analyzing-sub">Scoring dimensions and identifying gaps…</p>
        <div class="progress-bar-outer" aria-hidden="true">
          <div class="progress-bar-inner" style="width: {progress}%;"></div>
        </div>
        <p class="analyzing-note">
          This keeps running in the background — feel free to leave this page and come back to it whenever you like.
        </p>
        <button type="button" class="analyzing-leave" on:click={leaveAnalyzing}>
          <i class="times icon" aria-hidden="true"></i>
          Leave it running
        </button>
      </div>
    {/if}
  </div>
</div>

<style>
  /* ---------- Frame ---------- */
  .creator-frame {
    width: 100%;
  }

  .creator-frame.wizard {
    max-width: 620px;
    margin: 0 auto;
    padding: var(--space-8) var(--space-5) var(--space-6);
  }

  .eyebrow-row {
    display: flex;
    justify-content: center;
    margin-bottom: var(--space-3);
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-3);
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: var(--accent-pink-ink);
    background: var(--accent-pink-soft);
    border-radius: var(--radius-pill);
  }

  .eyebrow :global(i.icon) { margin: 0; }

  .step-title {
    text-align: center;
    font-size: var(--text-xl);
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 var(--space-2) 0;
    line-height: 1.2;
  }

  .step-sub {
    text-align: center;
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    margin: 0 0 var(--space-5) 0;
  }

  /* ---------- Cards ---------- */
  .creator,
  .title-card {
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-card);
    padding: var(--space-5);
  }

  .title-card {
    margin-bottom: var(--space-4);
  }

  /* ---------- Segmented control ---------- */
  .segment {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    padding: 4px;
    background: var(--surface-sunken);
    border-radius: var(--radius-input);
    margin-bottom: var(--space-5);
  }

  .seg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: transparent;
    border: 0;
    border-radius: calc(var(--radius-input) - 2px);
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    font-weight: 500;
    cursor: pointer;
    transition: background .15s, color .15s, box-shadow .15s;
  }

  .seg-btn :global(i.icon) { margin: 0; }

  .seg-btn:hover:not(.active) {
    color: var(--grey);
    background: rgba(0, 0, 0, 0.02);
  }

  .seg-btn.active {
    background: var(--surface);
    color: var(--primary);
    box-shadow: var(--shadow-card);
  }

  .seg-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

  /* ---------- Field area ---------- */
  .field-area {
    margin-bottom: var(--space-4);
  }

  .labelled-field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin-top: var(--space-4);
  }

  .field-label {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey);
  }

  .optional {
    color: var(--grey-secondary);
    font-weight: 400;
    margin-left: var(--space-1);
  }

  .field-help {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    margin: 0;
  }

  .text-input,
  .paste-area,
  .select-input {
    width: 100%;
    padding: var(--space-3);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    background: var(--surface);
    font: inherit;
    color: var(--grey);
    transition: border-color .15s, box-shadow .15s;
  }

  .paste-area { resize: vertical; min-height: 180px; line-height: 1.5; }

  .text-input:focus,
  .paste-area:focus,
  .select-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  .select-wrapper {
    position: relative;
  }

  /* ---------- Brief search combobox ---------- */
  .brief-combobox {
    position: relative;
  }

  .combo-icon {
    position: absolute;
    left: var(--space-3);
    top: 50%;
    transform: translateY(-50%);
    color: var(--grey-secondary);
    pointer-events: none;
  }

  .combo-input {
    padding-left: calc(var(--space-3) + 1.25rem);
    padding-right: calc(var(--space-3) + 1.5rem);
  }

  .combo-clear {
    position: absolute;
    right: var(--space-2);
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: 0;
    color: var(--grey-secondary);
    cursor: pointer;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .combo-clear:hover { background: rgba(0, 0, 0, 0.06); color: var(--grey); }
  .combo-clear :global(i.icon) { margin: 0; }

  .combo-list {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 4px);
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    box-shadow: var(--shadow-pop, 0 10px 30px rgba(0,0,0,.12));
    padding: 4px;
    margin: 0;
    list-style: none;
    z-index: 20;
    max-height: 340px;
    overflow: auto;
  }

  .combo-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: var(--space-2) var(--space-3);
    border-radius: calc(var(--radius-input) - 2px);
    cursor: pointer;
  }

  .combo-item.active { background: var(--surface-sunken, #f4f5f7); }
  .combo-item.selected { background: rgba(5, 108, 242, 0.08); }

  .combo-item-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
  }

  .combo-item-name {
    color: var(--grey);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .combo-badge {
    flex-shrink: 0;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: var(--radius-pill, 999px);
    letter-spacing: 0.02em;
  }

  .combo-badge.tone-ok     { background: var(--status-success-soft, #e6f7ee); color: var(--status-success-ink, #0e7f47); }
  .combo-badge.tone-warn   { background: var(--status-warn-soft,    #fff4d1); color: var(--status-warn-ink,    #a67a00); }
  .combo-badge.tone-danger { background: var(--status-danger-soft,  #fde8ec); color: var(--status-danger-ink,  #8b1c2d); }
  .combo-badge.tone-none   { display: none; }

  .combo-item-meta {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: var(--grey-secondary);
    font-size: var(--text-xs, 0.8rem);
  }

  .meta-sep { opacity: 0.6; }

  .combo-hint {
    padding: var(--space-2) var(--space-3);
    font-size: var(--text-xs, 0.8rem);
    color: var(--grey-secondary);
    border-top: 1px solid var(--border-color);
    margin-top: 4px;
    list-style: none;
  }

  .combo-empty {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 4px);
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    padding: var(--space-3);
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    z-index: 20;
  }

  .select-input {
    appearance: none;
    -webkit-appearance: none;
    padding-right: var(--space-6);
    cursor: pointer;
  }

  .chevron {
    position: absolute;
    right: var(--space-3);
    top: 50%;
    transform: translateY(-50%);
    color: var(--grey-secondary);
    pointer-events: none;
  }

  /* ---------- Dropzone ---------- */
  .dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-8) var(--space-5);
    border: 2px dashed var(--border-strong);
    border-radius: var(--radius-card);
    background: var(--surface-sunken);
    cursor: pointer;
    text-align: center;
    transition: background-color .15s, border-color .15s;
    min-height: 220px;
  }

  .dropzone:hover { background: var(--surface-hover); }
  .dropzone.dragging {
    background: var(--accent-pink-soft);
    border-color: var(--accent-pink);
  }

  .file-input { position: absolute; opacity: 0; pointer-events: none; width: 0; height: 0; }

  .dz-icon-chip {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--accent-pink-soft);
    color: var(--accent-pink-ink);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--space-4);
    font-size: 1.75rem;
  }
  .dz-icon-chip :global(i.icon) { margin: 0; font-size: 1.4rem; }

  .dz-text { margin-bottom: var(--space-2); }
  .dz-text strong {
    display: block;
    color: var(--grey);
    font-size: var(--text-base);
    font-weight: 500;
    margin-bottom: var(--space-1);
  }
  .dz-text span {
    display: block;
    color: var(--grey-secondary);
    font-size: var(--text-sm);
  }
  .dz-text em {
    font-style: normal;
    color: var(--primary);
    font-weight: 500;
  }

  .dz-formats {
    font-size: var(--text-xs);
    color: var(--grey-secondary);
    letter-spacing: .1em;
    text-transform: uppercase;
  }

  /* ---------- Selected file ---------- */
  .file-selected {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-4);
    background: var(--surface-sunken);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
  }

  .file-icon-chip {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-input);
    background: var(--accent-pink-soft);
    color: var(--accent-pink-ink);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .file-icon-chip :global(i.icon) { margin: 0; font-size: 1.25rem; }

  .file-meta { flex: 1; min-width: 0; }

  .file-name {
    color: var(--grey);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .file-size {
    color: var(--grey-secondary);
    font-size: var(--text-sm);
  }

  .change-link {
    background: transparent;
    border: none;
    color: var(--grey-secondary);
    cursor: pointer;
    padding: var(--space-2);
    border-radius: 50%;
    line-height: 1;
    transition: background .15s, color .15s;
  }
  .change-link:hover { background: rgba(0, 0, 0, .05); color: var(--grey); }
  .change-link :global(i.icon) { margin: 0; }
  .change-link:focus-visible { outline: none; box-shadow: var(--focus-ring); }

  /* ---------- CTA ---------- */
  .analyse-btn {
    width: 100%;
    background: var(--primary);
    color: var(--white);
    border: none;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-cta);
    font-size: var(--text-base);
    font-weight: 500;
    cursor: pointer;
    margin-top: var(--space-5);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    box-shadow: var(--shadow-cta);
    transition: filter .15s, box-shadow .15s;
  }
  .analyse-btn:hover:not(:disabled) { filter: brightness(.95); }
  .analyse-btn:disabled {
    /* Stay visibly blue even when disabled — just dim + disable pointer. */
    background: var(--primary);
    color: var(--white);
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
  .analyse-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }
  .analyse-btn :global(i.icon) { margin: 0; }

  /* ---------- Analyzing state ---------- */
  .creator.analyzing {
    padding: var(--space-8) var(--space-5);
  }

  .analyzing-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    text-align: center;
  }

  .loader-ring {
    width: 56px;
    height: 56px;
    border: 4px solid var(--accent-pink-soft);
    border-top-color: var(--accent-pink);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: var(--space-5);
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .analyzing-title {
    font-size: var(--text-xl);
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 var(--space-2) 0;
  }
  .analyzing-sub {
    color: var(--grey-secondary);
    margin: 0 0 var(--space-5) 0;
  }

  .progress-bar-outer {
    width: 100%;
    max-width: 320px;
    height: 4px;
    background: var(--accent-pink-soft);
    border-radius: var(--radius-pill);
    overflow: hidden;
  }
  .progress-bar-inner {
    height: 100%;
    background: var(--accent-pink);
    transition: width .3s linear;
  }

  .analyzing-note {
    max-width: 380px;
    margin: var(--space-5) auto 0;
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    line-height: 1.5;
    text-align: center;
  }

  .analyzing-leave {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-3);
    padding: var(--space-2) var(--space-4);
    background: transparent;
    color: var(--grey);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    font-size: var(--text-sm);
    font-weight: 500;
    cursor: pointer;
    transition: background .15s, color .15s, border-color .15s;
  }

  .analyzing-leave :global(i.icon) { margin: 0; }

  .analyzing-leave:hover {
    background: rgba(0, 0, 0, 0.04);
    color: var(--grey-darker, var(--grey));
    border-color: var(--border-strong, var(--border-color));
  }

  .analyzing-leave:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 640px) {
    .creator-frame.wizard { padding: var(--space-5) var(--space-4); }
    .segment { grid-template-columns: 1fr; }
    .seg-btn { justify-content: flex-start; }
    .dropzone { padding: var(--space-6) var(--space-4); min-height: 200px; }
  }
</style>
